import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Form } from "antd";
import { useDispatch } from "react-redux";

// Shared UI Library Components
import {
  CustomButton,
  DropdownElement,
} from "@ppmcore/seven-ppm-core-shared-components-react";

// Styles
import './confirmation-booking-appointment.scss';

// Store entities
import { updateAppointmentsSettings } from "../../../../../store/session-settings/session-settings.thunks";

// Models
import { IAppointmentSettings } from "../../../../../interfaces/session-settings.interfaces";

// Configs
import { CSessionConfirmationTime } from "../../../../../constantes/sessions.constants";

const timeOptions = CSessionConfirmationTime.map((value) => ({
  value: `${ value }`,
  label: `${ value } minutes`
}));

type TFormValue = {
  max_approval_hours: string;
};

export type TConfirmationBookingAppointmentRef = {
  isHasChanges: () => boolean;
  resetState: () => void;
};

type TConfirmationBookingAppointmentProps = {
  appointmentsSettings: IAppointmentSettings;
};

export const ConfirmationBookingAppointment = forwardRef<TConfirmationBookingAppointmentRef, TConfirmationBookingAppointmentProps>((
  { appointmentsSettings }: TConfirmationBookingAppointmentProps, _ref
) => {
  const [form] = Form.useForm<TFormValue>();

  const dispatch = useDispatch<any>();

  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [savingChanges, setSavingChanges] = useState<boolean>(false);

  const saveChanges = async (): Promise<void> => {
    setSavingChanges(() => true);
    const { payload } = await dispatch(updateAppointmentsSettings({
      value: form.getFieldsValue().max_approval_hours,
      key: 'max_approval_hours',
    }));
    setSavingChanges(() => false);
    if (!payload) return;
    setHasChanges(false);
  }

  const onValuesChange = (_: { [key in keyof TFormValue]: string }, {
    max_approval_hours
  }: TFormValue): void => {
    setHasChanges(appointmentsSettings?.max_approval_hours != +max_approval_hours);
  }

  useImperativeHandle(_ref, () => ({
    isHasChanges(): boolean {
      return hasChanges;
    },
    resetState(): void {
      form.setFieldValue('max_approval_hours', `${ appointmentsSettings?.max_approval_hours || 1 }`);
      setHasChanges(false);
      setSavingChanges(false);
    }
  }));

  useEffect(() => {
    if (!appointmentsSettings) return;
    form.setFieldValue('max_approval_hours', `${ appointmentsSettings?.max_approval_hours || 1 }`);
  }, [appointmentsSettings]);

  return (<div className="confirmation-booking-appointment">
    <div className="confirmation-booking-appointment--title">
      Confirmation time for booking an appointment
    </div>
    <div className="confirmation-booking-appointment--description">
      The Consultant is given time to confirm the booking. If the confirmation is not made within this time, the
      Client will be refunded the full amount of money.
    </div>
    <Form className="confirmation-booking-appointment--select" form={ form } onValuesChange={ onValuesChange }>
      <Form.Item name="max_approval_hours">
        <DropdownElement options={ timeOptions }
                         withoutAll={ true }
                         placeholder={ 'Confirmation time' }/>
      </Form.Item>

      <div className="select-action" hidden={ !hasChanges }>
        <CustomButton loading={ savingChanges }
                      text={ 'Save' }
                      size={ 'small' }
                      onClick={ saveChanges }/>
      </div>
    </Form>
  </div>);
});
